import React, {useRef} from 'react'

import {IconButton, P} from '../index.js'
import {FieldProps} from './types.js'
import ControlLabel from './ControlLabel.js'
import {componentMargins, field, formSpacing} from './styles.js'

// readOnly hides the pick/remove actions but still shows the current file name
export interface FileUploadProps extends FieldProps {
    fileName?: string
    // Extensions including the dot, e.g. '.pdf' or ['.pdf', '.png']
    accept?: string | string[]
    maxSize?: number // in MB
    // Called with the file as a data URL, or with ('', '') when the file is removed
    onChange?: (fileName: string, data: string, type?: string) => void
}

const FileUpload: React.FC<FileUploadProps> = ({style, label, info, disabled, readOnly, fileName, accept, maxSize, onChange}) => {
    const fileControlRef = useRef<HTMLInputElement | null>(null)

    const upload = (event: React.MouseEvent) => {
        event.preventDefault()
        if (disabled || readOnly) return
        fileControlRef.current.click()
    }

    const saveFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault()

        const file = event.target.files?.[0]

        // Reset so picking the same file again (e.g. after removing it) fires change again
        event.target.value = ''

        // Cancelling the dialog can fire change with an empty FileList
        if (!file) return

        const reader = new FileReader()

        const extension = file.name.split('.').pop().toLowerCase()

        // Compare whole extensions, a string accept may be comma separated ('.pdf,.png')
        const acceptedExtensions = (Array.isArray(accept) ? accept : (accept || '').split(','))
            .map((ext) => ext.trim().toLowerCase())
            .filter(Boolean)

        if (acceptedExtensions.length && !acceptedExtensions.includes(`.${extension}`)) {
            alert(`Bestandstype ${extension} niet toegestaan.`)
            return
        }

        const fileName = file.name

        if (maxSize && file.size > maxSize * 1024 * 1024) {
            alert(`Bestand is te groot, maximaal ${maxSize}mb toegestaan.`)
            return
        }

        reader.onloadend = (event) => {
            onChange?.(fileName, event.target.result as string, file.type)
        }

        reader.readAsDataURL(file)
    }

    const removeFile = (event: React.MouseEvent) => {
        event.preventDefault()
        if (disabled || readOnly) return
        onChange?.('', '')
    }

    const defaultStyle: React.CSSProperties = {display: 'flex', alignItems: 'center', justifyContent: 'space-between', columnGap: formSpacing.columnGap, height: field.height, ...componentMargins.fileUpload}

    if (style) {
        Object.keys(style).map((key) => {
            defaultStyle[key] = style[key]
        })
    }

    return (
        <div style={defaultStyle}>
            {label &&
                <ControlLabel info={info} disabled={disabled}>
                    <div style={{whiteSpace: 'nowrap'}}>{label}</div>
                </ControlLabel>
            }

            <div style={{display: 'flex', alignItems: 'center'}}>

                {fileName &&
                    <P disabled={disabled}>{fileName}</P>
                }

                {!readOnly &&
                    <IconButton disabled={disabled} aria-label='Bestand kiezen' onClick={upload}>
                        <i className='mdi mdi-upload' title='Bestand kiezen' />
                    </IconButton>
                }

                {fileName && !readOnly &&
                    <IconButton disabled={disabled} aria-label='Verwijderen' onClick={removeFile}>
                        <i className='mdi mdi-delete' title='Verwijderen' />
                    </IconButton>
                }
            </div>

            <input
                style={{display: 'none'}}
                type='file'
                accept={Array.isArray(accept) ? accept.join(',') : accept}
                disabled={disabled || readOnly}
                ref={fileControlRef}
                onChange={saveFile}
            />
        </div>
    )
}

export default FileUpload
